import { createImage, readFile } from '@shared/utils/file-utils';

import { compressImageForWeb, compressImageToWebP } from '@shared/utils/image-compressor';

import { FormValues } from '../types';

interface ProcessFormDataConfig {
  imageFields: string[];
  musicFields: string[];
  fileFields?: string[];
  gameType?: 'memory' | 'miner' | 'maze' | 'slot' | 'capybara' | 'flappy';
}

export const processFormData = async (
  data: FormValues,
  gameKey: { gameBuildId: string },
  trimmedAudioFile: File | null,
  config: ProcessFormDataConfig
): Promise<FormData> => {
  const formData = new FormData();
  const jsonData: any = {
    _id: gameKey.gameBuildId,
  };

  // Обработка всех полей
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];

      if (config.imageFields.includes(key)) {
        // Обработка изображений
        let image = value;
        if (image instanceof File) {
          console.log(`Processing image field ${key} for gameType: ${config.gameType}`);
          
          // Для Flappy Bird используем обрезку в круг, для остальных - в квадрат
          if (config.gameType === 'flappy') {
            const { cropImageToCircle } = await import('@pages/Constructors/ClientFlappy/client.utils');
            image = await cropImageToCircle(image);
          } else {
            image = await cropImageToSquare(image, config.gameType);
          }
          
          console.log(`Final image for ${key}:`, {
            name: image.name,
            type: image.type,
            size: image.size
          });
          formData.append(key, image);
        } else if (typeof image === 'string' && image) {
          console.log(`Processing image URL field ${key} for gameType: ${config.gameType}`);
          
          // Для Flappy Bird используем обрезку в круг, для остальных - в квадрат
          if (config.gameType === 'flappy') {
            const { cropUrlImageToCircle } = await import('@pages/Constructors/ClientFlappy/client.utils');
            image = await cropUrlImageToCircle(image, `${key}.webp`);
          } else {
            image = await cropUrlImageToSquare(image, `${key}.jpg`, config.gameType);
          }
          
          console.log(`Final image URL for ${key}:`, {
            name: image.name,
            type: image.type,
            size: image.size
          });
          formData.append(key, image);
        }
      } else if (config.musicFields.includes(key)) {
        // Обработка музыки
        if (trimmedAudioFile && key === 'customMusicFile') { // Пока что завязка на конкретное имя
          formData.append(key, trimmedAudioFile);
        } else if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'string' && value) {
          jsonData.musicUrl = value;
        }
      } else if (config.fileFields?.includes(key)) {
        // Обработка файлов для скачивания
        // Специальная обработка для urlButton: если File - отправляем как fileDownload, если строка - в jsonData.urlButton
        if (key === 'urlButton') {
          if (value instanceof File) {
            console.log(`Processing urlButton file field:`, {
              name: value.name,
              type: value.type,
              size: value.size
            });
            // Отправляем файл как fileDownload, сервер установит fileDownloadUrl после загрузки
            formData.append('fileDownload', value);
            // Очищаем urlButton в JSON, так как используется файл
            jsonData.urlButton = '';
          } else if (typeof value === 'string' && value) {
            // Если строка - записываем в urlButton, а не в fileDownloadUrl
            jsonData.urlButton = value;
            // Очищаем fileDownloadUrl, если был установлен ранее
            jsonData.fileDownloadUrl = '';
          } else {
            // Если null/undefined - очищаем оба поля
            jsonData.urlButton = '';
            jsonData.fileDownloadUrl = '';
          }
        } else {
          // Обработка других fileFields (если есть)
          if (value instanceof File) {
            console.log(`Processing file field ${key}:`, {
              name: value.name,
              type: value.type,
              size: value.size
            });
            formData.append(key, value);
            // Не устанавливаем jsonData.fileDownloadUrl для новых файлов - сервер сам установит URL после загрузки
          } else if (typeof value === 'string' && value) {
            jsonData.fileDownloadUrl = value;
          }
        }
      } else {
        // Остальные (текстовые) поля
        jsonData[key] = value;
      }
    }
  }

  formData.append('jsonData', JSON.stringify(jsonData));
  return formData;
};

const cropImageToSquare = async (file: File, gameType?: string): Promise<File> => {
  const img = await createImage(await readFile(file));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  const size = Math.min(img.width, img.height);
  canvas.width = size;
  canvas.height = size;
  
  const offsetX = (img.width - size) / 2;
  const offsetY = (img.height - size) / 2;
  
  ctx?.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
  
  const croppedFile = await new Promise<File>(async (resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          // Создаем файл из blob после обрезки
          const tempFile = new File([blob], file.name, { type: file.type });
          
          // Применяем дополнительное сжатие с помощью compressorjs
          console.log(`Compressing image for gameType: ${gameType}`);
          
          // Применяем сжатие в WebP для Capybara и Flappy Bird
          if (gameType === 'capybara' || gameType === 'flappy') {
            const compressionResult = await compressImageToWebP(tempFile);
            
            console.log('Image compression result (WebP):', {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
              compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
            });
            
            resolve(compressionResult.file);
          } else {
            const compressionResult = await compressImageForWeb(tempFile);
            
            console.log('Image compression result:', {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
              compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
            });
            
            resolve(compressionResult.file);
          }
        } catch (error) {
          console.error('Compression failed, using original cropped file:', error);
          // Если сжатие не удалось, возвращаем оригинальный файл в WebP для Flappy Bird
          const fallbackType = (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : file.type;
          resolve(new File([blob], file.name, { type: fallbackType }));
        }
      } else {
        resolve(file);
      }
    }, (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : file.type, 0.95); // Высокое качество для обрезки
  });
  
  return croppedFile;
};

const cropUrlImageToSquare = async (url: string, fileName: string, gameType?: string): Promise<File> => {
  try {
    const img = await createImage(url);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const size = Math.min(img.width, img.height);
    canvas.width = size;
    canvas.height = size;
    
    const offsetX = (img.width - size) / 2;
    const offsetY = (img.height - size) / 2;
    
    ctx?.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
    
    const croppedFile = await new Promise<File>(async (resolve, reject) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            // Создаем файл из blob после обрезки
            // Для Flappy Bird и Capybara используем WebP
            const fileType = (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : 'image/jpeg';
            const tempFile = new File([blob], fileName, { type: fileType });
            
            // Применяем дополнительное сжатие с помощью compressorjs
            console.log(`Compressing URL image for gameType: ${gameType}`);
            
            // Применяем сжатие в WebP для Capybara и Flappy Bird
            if (gameType === 'capybara' || gameType === 'flappy') {
              const compressionResult = await compressImageToWebP(tempFile);
              
              console.log('Image compression result (WebP):', {
                originalSize: compressionResult.originalSize,
                compressedSize: compressionResult.compressedSize,
                compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
              });
              
              resolve(compressionResult.file);
            } else {
              const compressionResult = await compressImageForWeb(tempFile);
              
              console.log('Image compression result:', {
                originalSize: compressionResult.originalSize,
                compressedSize: compressionResult.compressedSize,
                compressionRatio: compressionResult.compressionRatio.toFixed(2) + '%'
              });
              
              resolve(compressionResult.file);
            }
          } catch (error) {
            console.error('Compression failed, using original cropped file:', error);
            // Если сжатие не удалось, возвращаем оригинальный файл в WebP для Flappy Bird
            const fallbackType = (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : 'image/jpeg';
            resolve(new File([blob], fileName, { type: fallbackType }));
          }
        } else {
          const fallbackType = (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : 'image/jpeg';
          resolve(new File([], fileName, { type: fallbackType }));
        }
      }, (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : 'image/jpeg', 0.95); // Высокое качество для обрезки
    });
    
    return croppedFile;
  } catch (error) {
    console.error('Error processing image URL:', error);
    const fallbackType = (gameType === 'flappy' || gameType === 'capybara') ? 'image/webp' : 'image/jpeg';
    return new File([], fileName, { type: fallbackType });
  }
}; 